import { VerticalBox } from "src/components/common/box/VerticalBox";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { ConfiguratorTreeItemModel } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeItemModel";
import { Typography, useTheme } from "@mui/material";
import { mergeSx } from "src/utils/styles.ts";
import { alpha } from "@mui/material/styles";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import {
	faArrowUp,
	faChevronDown,
	faChevronRight,
	faClone,
	faInfoCircle,
	faPlus,
	faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import i18n from "i18next";
import { AavoContextMenu } from "src/components/common/contextMenu/AavoContextMenu.tsx";
import { useContextMenu } from "src/components/common/contextMenu/useContextMenu.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { PartConfiguratorVersionInfoView } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/PartConfiguratorVersionInfoView.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import {
	getAddableTreeItemSubConfiguratorComponents,
	getTreeItemSubConfiguratorComponents,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/treeItemModel/configuratorTreeItemQueries.ts";
import { useMemo } from "react";
import { getConfiguratorFormErrors } from "src/components/views/erp/configurator/configuratorForm/validation.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mui/material";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { getPartConfiguratorAgentContextData } from "src/components/views/erp/configurator/configuratorForm/utils/partConfiguratorTypeUtils.ts";
import { getTitlePrefix } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/configuratorTreeTitlePrefix.ts";

export interface RecursiveConfiguratorTreeItemProps {
	treeItemModel: ConfiguratorTreeItemModel;
	selectedItemKey: string | undefined;
	depth: number;
	parentTreeItemModel: ConfiguratorTreeItemModel | undefined;
	selectTreeItem: (key: string) => Promise<undefined | "interrupted">;
	toggleTreeItemExpansion: (key: string) => void;
	onAddSubConfiguratorClicked: (parentKey: string) => Promise<unknown>;
	deleteSubConfiguratorListItem: (key: string) => Promise<unknown>;
	cloneSubConfiguratorListItem: (key: string) => Promise<unknown>;
	updateSingleVersion?: (key: string) => Promise<unknown>;
	readonly?: boolean;
	allowInputErrors: boolean;
}

export const RecursiveConfiguratorTreeItem = (props: RecursiveConfiguratorTreeItemProps) => {
	const {
		treeItemModel,
		selectedItemKey,
		depth,
		selectTreeItem,
		toggleTreeItemExpansion,
		onAddSubConfiguratorClicked,
		readonly,
		allowInputErrors,
	} = props;

	const { palette } = useTheme();
	const [openContextMenu, contextMenuState] = useContextMenu();

	const isSelected = treeItemModel.key === selectedItemKey;

	const addableSubConfiguratorComponents = getAddableTreeItemSubConfiguratorComponents(treeItemModel);

	const errors = useMemo(() => getConfiguratorFormErrors(treeItemModel.components), [treeItemModel.components]);
	const hasErrors = !allowInputErrors && errors.length > 0;

	return (
		<>
			<HorizontalBox
				component={"li"}
				onClick={async () => {
					await selectTreeItem(treeItemModel.key);
				}}
				onContextMenu={async (e) => {
					e.preventDefault();
					const selectItemResult = await selectTreeItem(treeItemModel.key);
					if (selectItemResult === "interrupted") return;
					openContextMenu({ mouseEvent: e, content: <TreeItemContextMenu {...props} /> });
				}}
				sx={mergeSx(
					{
						flex: "fit-content 0 0",
						paddingY: 1.25,
						paddingLeft: 1 + depth * 2,
						":hover": {
							backgroundColor: palette.grey[100],
						},
					},
					isSelected && {
						backgroundColor: alpha(palette.primary.light, 0.1),
					},
				)}
			>
				<AavoContextMenu state={contextMenuState} />
				<HorizontalBox
					sx={{
						position: "relative",
						alignItems: "center",
						gap: 1,
					}}
				>
					<VerticalBox>
						<Typography
							sx={{
								fontWeight: "bold",
								marginRight: 1,
								alignItems: "center",
							}}
						>
							{`${getTitlePrefix(depth)} ${treeItemModel.label}`}
						</Typography>
					</VerticalBox>
					<TreeItemErrorIndicator errors={errors} />
					{addableSubConfiguratorComponents.length > 0 && !readonly && (
						<AsyncButton
							icon={faPlus}
							tooltip={
								hasErrors ?
									i18n.t("fix_input_errors_before_adding_sub_configurations")
								:	i18n.t("add_sub_configuration")
							}
							size={"small"}
							disabled={hasErrors}
							sx={{
								paddingX: 0.5,
							}}
							onClick={async () => {
								await onAddSubConfiguratorClicked(treeItemModel.key);
							}}
						/>
					)}
					{treeItemModel.children.length > 0 && (
						<AavoButton
							icon={treeItemModel.expanded ? faChevronDown : faChevronRight}
							color={"black"}
							sx={{
								fontSize: "0.75rem",
								padding: 1,
							}}
							onClick={(e) => {
								toggleTreeItemExpansion(treeItemModel.key);
								e.stopPropagation();
							}}
						/>
					)}
				</HorizontalBox>
			</HorizontalBox>
			{treeItemModel.expanded &&
				treeItemModel.children.map((childState) => (
					<RecursiveConfiguratorTreeItem
						{...props}
						key={childState.key}
						treeItemModel={childState}
						depth={depth + 1}
						parentTreeItemModel={treeItemModel}
					/>
				))}
		</>
	);
};

interface TreeItemErrorIndicatorProps {
	errors: string[];
}

const TreeItemErrorIndicator = ({ errors }: TreeItemErrorIndicatorProps) => {
	const { palette } = useTheme();

	if (errors.length === 0) return;

	return (
		<Tooltip
			title={
				<>
					{errors.map((e, idx) => (
						<Typography key={idx}>{e}</Typography>
					))}
				</>
			}
		>
			<FontAwesomeIcon icon={faInfoCircle} color={palette.error.main} />
		</Tooltip>
	);
};

const TreeItemContextMenu = ({
	treeItemModel,
	onAddSubConfiguratorClicked,
	deleteSubConfiguratorListItem,
	cloneSubConfiguratorListItem,
	updateSingleVersion,
	readonly,
	parentTreeItemModel,
}: RecursiveConfiguratorTreeItemProps) => {
	const showConfirmDialog = useConfirmDialog();
	const { openDialog } = useGenericDialog();

	const addableSubConfiguratorComponents = useMemo(
		() => getAddableTreeItemSubConfiguratorComponents(treeItemModel),
		[treeItemModel],
	);

	const parentSubConfiguratorComponents = useMemo(
		() => (parentTreeItemModel ? getTreeItemSubConfiguratorComponents(parentTreeItemModel) : []),
		[parentTreeItemModel],
	);

	const parentAddableSubConfiguratorComponents = useMemo(
		() => (parentTreeItemModel ? getAddableTreeItemSubConfiguratorComponents(parentTreeItemModel) : []),
		[parentTreeItemModel],
	);

	const parentSubConfiguratorComponentForSelf = parentSubConfiguratorComponents.find(
		(component) => component.name === treeItemModel.selfConfiguratorComponentName,
	);
	const selfCanBeCloned = parentAddableSubConfiguratorComponents.some(
		(component) => component.name === treeItemModel.selfConfiguratorComponentName,
	);
	const selfCanBeRemoved = parentSubConfiguratorComponentForSelf?.removeEnabled ?? false;
	const isSubConfigurator = treeItemModel.configuratorType.type === "subConfigurator";

	return [
		!readonly && addableSubConfiguratorComponents.length > 0 && (
			<AsyncMenuButton
				key={"addSubConfigurator"}
				icon={faPlus}
				label={i18n.t("add_sub_configuration")}
				onClick={async () => {
					await onAddSubConfiguratorClicked(treeItemModel.key);
				}}
			/>
		),
		!readonly && selfCanBeRemoved && (
			<AsyncMenuButton
				key={"remove"}
				label={i18n.t("remove")}
				icon={faTrash}
				onClick={async () => {
					const confirmed = await showConfirmDialog();
					if (!confirmed) return;

					await deleteSubConfiguratorListItem(treeItemModel.key);
				}}
			/>
		),
		!readonly && selfCanBeCloned && (
			<AsyncMenuButton
				key={"clone"}
				label={i18n.t("clone")}
				icon={faClone}
				onClick={async () => {
					await cloneSubConfiguratorListItem(treeItemModel.key);
				}}
			/>
		),
		<AsyncMenuButton
			key={"revisionInfo"}
			icon={faInfoCircle}
			label={i18n.t("part_revision_info")}
			onClick={() => {
				openDialog({
					title: i18n.t("part_revision_info"),
					size: "sm",
					content: <PartConfiguratorVersionInfoView configuratorType={treeItemModel.configuratorType} />,
					agentContext: {
						key: defineAgentUiContextKey("erp.configurator.partRevisionInfo.dialog"),
						title: i18n.t("part_revision_info"),
						pathSegments: [treeItemModel.label],
						data: {
							...getPartConfiguratorAgentContextData(treeItemModel.configuratorType),
							treeItemKey: treeItemModel.key,
						},
					},
				});
			}}
		/>,
		!readonly && isSubConfigurator && updateSingleVersion && (
			<AsyncMenuButton
				key={"updateVersion"}
				icon={faArrowUp}
				label={i18n.t("update_version")}
				onClick={async () => {
					await updateSingleVersion(treeItemModel.key);
				}}
			/>
		),
	];
};
